import { useCallback } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import { getRouteApi, useNavigate } from '@tanstack/react-router'
import { routes } from 'commons/constants/routes'
import { bankAccountNumberClear } from 'commons/utils'
import { Toast } from 'ui/components'
import { handleFormSubmissionError } from 'ui/components/Form/handleFormSubmissionError'
import { useConfirmation } from 'ui/hooks'

import { hooks } from '../../hooks'
import { BankAccountForm } from '../../types'

const routeApi = getRouteApi('/_app/bank-accounts/new')

const emptyForm: BankAccountForm = {
  active: true,
  closingDate: '',
  id: 0,
  internalTransfersType: 3,
  licenseIds: [],
  notes: '',
  number: '',
  openingDate: ''
}

export const useCreate = () => {
  const navigate = useNavigate({ from: routes.bank.bankAccounts.create })
  const companyId = +routeApi.useSearch({ select: (s) => s.companyId })
  const { data, isFetching } = hooks.useCreatePage({ companyId })
  const returnUrl = routeApi.useSearch({ select: (s) => s.returnUrl })
  const cancelManage = useCallback(() => history.back(), [])

  const {
    mutate: updateBankAccount,
    isPending,
    isSuccess
  } = hooks.useUpdate({
    onError: (error) => handleFormSubmissionError(error, formApi),
    onSuccess: async () => {
      Toast.successSmall('Bank account has been saved.')
      if (returnUrl) {
        await navigate({ to: returnUrl as never })
      }
    }
  })

  const formApi = useForm({
    defaultValues: emptyForm,
    onSubmit: ({ value }) =>
      updateBankAccount({
        ...value,
        companyId,
        number: bankAccountNumberClear(value.number)
      })
  })

  const { handleSubmit } = formApi
  const value = useStore(formApi.store, (s) => s.values)

  const { open: openSaveConfirmation, Confirmation: SaveConfirmation } = useConfirmation({
    message: (
      <>
        This bank account is not assigned to any
        <br />
        license. Do you want to proceed?
      </>
    ),
    onConfirm: handleSubmit,
    confirmationButtonText: 'Submit',
    isConfirmationNegative: true
  })

  const submitForm = value.licenseIds?.length ? handleSubmit : openSaveConfirmation

  return {
    cancelManage,
    data,
    formApi,
    isFetching,
    isPending,
    isSuccess,
    SaveConfirmation,
    submitForm
  }
}
